import { useEffect, useState, type ReactNode } from 'react';
import { t } from './i18n.js';
import { Link, useLocation } from './router.js';
import { useSession } from './session.js';
import { realtime } from './ws.js';

/**
 * The frame round every page: the brand, the tabs (sections), the submenu of the open section (none for a section
 * without pages, so the page starts higher), the footer. The user's name sits in the top links, not on a line of its own. On a phone the tabs and the submenu give way to one ☰ drawer that lists every section with its
 * pages, the help, the password page and the logout; the drawer closes on navigation, on the backdrop, on Escape.
 */

type SectionKey = 'news' | 'hub' | 'topics' | 'video' | 'feeds' | 'admin';
interface Item {
  to: string;
  label: string;
  /** Active only on the exact path (the section's own search page), not on everything under it. */
  exact?: boolean;
}
interface Section {
  key: SectionKey;
  to: string;
  label: string;
  items: Item[];
}

export function Frame({ children }: { children: ReactNode }) {
  const { session, logout, can } = useSession();
  const { path } = useLocation();
  const [ws, setWs] = useState(realtime.state);
  // the drawer remembers the path it was opened on, so navigating anywhere closes it by itself
  const [openAt, setOpenAt] = useState<string | null>(null);
  const menu = openAt === path;
  const setMenu = (open: boolean) => setOpenAt(open ? path : null);
  useEffect(() => realtime.onState(setWs), []);
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenAt(null);
    window.addEventListener('keydown', onKey);
    document.body.classList.add('menu-open');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('menu-open');
    };
  }, [menu]);

  const tab: SectionKey = path.startsWith('/admin') ? 'admin' : path.startsWith('/topics') ? 'topics' : path.startsWith('/video-hub') ? 'hub' : path.startsWith('/video') ? 'video' : path.startsWith('/feeds') ? 'feeds' : 'news';
  const sections: Section[] = [
    {
      key: 'news',
      to: '/',
      label: t('tabNews'),
      items: [
        { to: '/story/new', label: t('newStory') },
        { to: '/stories/day', label: t('dayStories') },
        { to: '/rundown', label: t('rundownGuide') },
        ...(can('rundown.open') ? [{ to: '/control', label: t('crTitle') }] : []),
        ...(can('rundown.open') ? [{ to: '/autocue', label: t('acMenu') }] : []),
        { to: '/stories/quick', label: t('quickFind') },
        { to: '/stories/archive', label: t('archive') },
      ],
    },
    { key: 'hub', to: '/video-hub', label: t('tabHub'), items: [] },
    {
      key: 'topics',
      to: '/topics',
      label: t('tabTopics'),
      items: [
        { to: '/topics', label: t('topicsMenuSearch'), exact: true },
        { to: '/topics/new', label: t('topicsMenuNew') },
      ],
    },
    {
      key: 'video',
      to: '/video',
      label: t('tabVideo'),
      // the archive is a MAM of its own (views, new item) from the top of the page
      items: [],
    },
    { key: 'feeds', to: '/feeds', label: t('tabFeeds'), items: [] },
    {
      key: 'admin',
      to: '/admin',
      label: t('tabAdmin'),
      items: [
        { to: '/admin/autocue', label: 'Autocue' },
        { to: '/admin/integrations', label: 'Playout / MOS' },
        ...(can('admin.users')
          ? [
              { to: '/admin/users', label: t('usersMenu') },
              { to: '/admin/roles', label: t('rolesMenu') },
            ]
          : []),
        ...(can('admin.config')
          ? [
              { to: '/admin/feeds', label: t('feedsMenu') },
              { to: '/admin/import', label: t('importMenu') },
              { to: '/admin/ai', label: t('aiMenu') },
              { to: '/admin/keys', label: t('keysMenu') },
            ]
          : []),
      ],
    },
  ];
  const current = sections.find((s) => s.key === tab)!;
  const isActive = (it: Item) => (it.exact ? path === it.to : path === it.to || path.startsWith(it.to));
  const doLogout = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    void logout();
  };

  return (
    <>
      <header className="header">
        <Link className="brand" to="/" title={t('home')}>
          <img className="brand-logo" src="/brand/airpost-logo.png" alt="AirPost" />
        </Link>
        <div className="header-right">
          <div className="toplinks">
            <span className={`status ${ws === 'live' ? 'ok' : ws === 'connecting' ? 'warn' : 'err'}`} title="WebSocket">
              {ws === 'live' ? t('live') : ws === 'connecting' ? t('reconnecting') : t('offline')}
            </span>
            <Link to="/">::</Link>
            <a href="/help/index.html" target="_blank" rel="noreferrer">
              {t('faq')}
            </a>
            <a href="/help/index.html" target="_blank" rel="noreferrer">
              {t('help')}
            </a>
            <a href="#print" onClick={(e) => (e.preventDefault(), window.print())}>
              {t('print')}
            </a>
            <span className="whoami" title={session?.user.username}>
              {session?.user.displayName}
            </span>
            <a href="#logout" onClick={doLogout}>
              {t('logout')}
            </a>
            <button type="button" className="burger" aria-label={t('menu')} aria-expanded={menu} aria-controls="drawer" onClick={() => setMenu(!menu)}>
              {menu ? '✕' : '☰'}
            </button>
          </div>
          <nav className="tabs" aria-label="menu">
            {sections.map((s) => (
              <Link key={s.key} className={`tab ${tab === s.key ? 'active' : ''}`} to={s.to}>
                {s.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>
      {current.items.length > 0 && (
        <div className="submenu">
          {current.items.map((it) => (
            <Link key={it.to} className={isActive(it) ? 'active' : ''} to={it.to}>
              {it.label}
            </Link>
          ))}
        </div>
      )}
      {menu && (
        <div className="drawer-backdrop" onClick={() => setMenu(false)}>
          <nav id="drawer" className="drawer" aria-label={t('menu')} onClick={(e) => e.stopPropagation()}>
            <div className="drawer-top">
              <span>{t('menu')}</span>
              <button type="button" className="drawer-close" aria-label={t('close')} onClick={() => setMenu(false)}>
                ✕
              </button>
            </div>
            {sections.map((s) => (
              <div key={s.key} className={`drawer-section${tab === s.key ? ' active' : ''}`}>
                <Link className="drawer-head" to={s.to}>
                  {s.label}
                </Link>
                {s.items.length > 0 && (
                  <div className="drawer-items">
                    {s.items.map((it) => (
                      <Link key={it.to} className={isActive(it) ? 'active' : ''} to={it.to}>
                        {it.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="drawer-foot">
              <span className="muted">
                {session?.user.displayName} · {session?.user.username}
              </span>
              <a href="/help/index.html" target="_blank" rel="noreferrer">
                {t('help')}
              </a>
              <Link to="/password">{t('changePassword')}</Link>
              <a href="#logout" onClick={doLogout}>
                {t('logout')}
              </a>
            </div>
          </nav>
        </div>
      )}
      <main>{children}</main>
      <div className="footer">
        <span>
          User : {session?.user.username} [<Link to="/password">{t('changePassword')}</Link>]
        </span>
        <span>AirPost 0.1 · {t('appSubtitle')} · Alpha Television Cyprus</span>
      </div>
    </>
  );
}

export function PageTitle({ title, hint }: { title: string; hint?: string }) {
  const { session } = useSession();
  return (
    <>
      <h1 className="page-title">
        {title} ({session ? 'TV' : ''})
      </h1>
      {hint && <p className="instruction">{hint}</p>}
    </>
  );
}
